import { debounce } from '@ember/runloop';

import type Highcharts from 'highcharts';

import type { DChartInterface } from 'webapp-types';
import { equal } from 'webapp/utils/common';
import { sanitize } from 'webapp/utils/html';

import type VisualizationHorizontalBarChart from 'webapp/models-custom/visualization/horizontal-bar-chart';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import BaseRenderer from 'webapp/d-chart/renderer/base';
import type DChartSeries from 'webapp/d-chart/series/series';

// a series as built here; its type comes from the chart options
type BarSeriesOptions = Omit<Highcharts.SeriesBarOptions, 'type' | 'data' | 'colorIndex'> & {
	data: DChartInterface.ChartDataPoint[];
	colorIndex?: string | number; // a class for custom colors
	dbxSeries: DChartSeries;
};

export default class HorizontalBarChartRenderer extends BaseRenderer {
	override tooltipArrowPosition = 'left'; // bcos it opens to the right

	override get visualization(): VisualizationHorizontalBarChart {
		return super.visualization as VisualizationHorizontalBarChart;
	}

	get hasLegend() {
		return !this.visualization.isLegendOff;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;

		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			chart: {
				type: 'bar',
				events: {
					render: function (this: Highcharts.Chart, _event: Event) {
						if (that.hasLegend) {
							debounce(that, that.centerLegendItems, this.legend, 500);
						}
					},
				},
			},
			plotOptions: {
				bar: {
					centerInCategory: true, // don't leave space for missing
					groupPadding: this.visualization.categories.length === 1 ? 0.2 : 0,
					borderRadius: 8,
					animation: {
						duration: this.dChart.isAnimated ? 500 : 0,
					},
					dataLabels: {
						formatter: function (
							this: Highcharts.Point & { point?: DChartInterface.ChartHoverPoint },
						) {
							return this.point!.dbxPoint.valueStr;
						},
					},
					events: {
						click: function (
							this: Highcharts.Series,
							event: Highcharts.SeriesClickEventObject,
						) {
							that.onPointClick(
								(event.point as DChartInterface.ChartHoverPoint).dbxPoint,
								event,
								(event.point as DChartInterface.ChartHoverPoint).graphic!.element,
							);
						},
					},
				},
			},
			xAxis: {
				labels: {
					padding: 0,
					formatter: (point: Highcharts.AxisLabelsFormatterContextObject) => {
						const maxWidth = that.dChart.maxWidth * 0.8, // allow to take up to 80% width
							maxChars = maxWidth / 8; // rough assumption
						let label = point.value as string;
						if (label.length > maxChars) {
							const chunk = label.substring(0, maxChars);
							if (!chunk.includes(' ')) {
								label = chunk + '...';
							}
						}
						return label;
					},
				},
				minPadding: 0.005,
				maxPadding: 0.005,
			},
			yAxis: [
				{
					title: {
						text: undefined,
					},
					labels: {
						formatter: function (point: Highcharts.AxisLabelsFormatterContextObject) {
							// allowing nbsp introduces weird formatting on horizontal bar
							// skip it since this axis is horizontal so is unlikely to break labels
							return that.valueAxisFormatter(point, false);
						},
					},
					gridLineWidth: 0,
				},
			],
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const categories = this.visualization.categories;

		const allSeries = this.visualization.series;
		const series: BarSeriesOptions[] = [];
		allSeries.forEach((s) => {
			const isStacked = s.isStacked,
				colorIndex = s.colorIndexOrClass,
				classNames = [`type-${s.type}`];
			if (isStacked) {
				classNames.push('stacked');
			}

			if (s.points[0]?.onClick) {
				classNames.push('series-clickable');
			}

			const sData: BarSeriesOptions = {
				className: classNames.join(' '),
				name: s.title,
				colorIndex,
				dbxSeries: s,
				legendIndex: allSeries.indexOf(s),
				data: [],
				dataLabels: [
					{
						enabled: s.showDataLabels,
					},
				],
			};

			if (isStacked) {
				sData.stack = s.stackId;
				sData.stacking = 'normal';
			} else {
				sData.stacking = undefined;
				sData.stack = `random-${Math.random()}`; // "resetting" stacked series
			}

			const pointsData = s.points.map((point) => {
				const categoryId = this.visualization.generateCategoryIdFromMetricSelectionData(
					point.metricSelectionData,
				);
				const props: DChartInterface.ChartDataPoint = {
					x: categories.indexOf(categories.find((c) => c.id === categoryId)!),
					y: point.value,
					colorIndex,
					dbxPoint: point,
				};
				return props;
			});

			sData.data = pointsData;

			series.push(sData);
		});

		return {
			xAxis: {
				categories: categories.map((c) => sanitize(c.label)),
			},
			legend: {
				enabled: this.hasLegend && series.length > 1,
			},
			series,
		};
	}

	override get sizeOptions(): Highcharts.Options {
		let options = super.sizeOptions;

		// const m = this.dChart.sizeModifier;

		options = this.mergeOptions(options, {
			legend: this.defaultLegendOptions,
		});

		return options;
	}

	override updateTheme(): void {
		this.visualization.updateColorIndexes();

		let options = this.themeOptions;

		// merge with data options since they set colorIndex
		options = this.mergeOptions(options, this.dataOptions);

		this.dChart.updateHChart(options);
	}

	override getTooltipForPoint(point: DChartPrimaryPoint) {
		const series = point.series;
		if (series?.isCompare) {
			// we want to show the "main" series value tooltip just with this compare selected
			const primaryPointOnPrimarySeries = series.primarySeries?.points.find((p) => {
				return (
					p.metricSelectionData &&
					equal(
						p.metricSelectionData.dimensionValues,
						point.metricSelectionData?.dimensionValues,
						true,
					)
				);
			});
			if (primaryPointOnPrimarySeries) {
				const comparePointOnPrimarySeries = primaryPointOnPrimarySeries.comparePoints.find(
					(c) => {
						return c.granulationLabel.ts === point.granulationLabel.ts;
					},
				);
				if (comparePointOnPrimarySeries) {
					const html = primaryPointOnPrimarySeries.getTooltipHtmlForPoint(
						comparePointOnPrimarySeries,
					);
					const typeClass = primaryPointOnPrimarySeries.tooltipTypeClass;
					return { html, typeClass };
				}
			}
		}

		return super.getTooltipForPoint(point);
	}
}
